import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ClipboardList, Plus, RefreshCw, Clock, Phone, Building, ChevronRight,
  Check, X, MessageSquare, AlertCircle, Calendar as CalendarIcon, User,
  Send, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const LANE_CONFIG = {
  new: { label: 'New / Untriaged', color: 'bg-blue-500', icon: ClipboardList },
  in_progress: { label: 'In Progress', color: 'bg-indigo-500', icon: ArrowRight },
  waiting_on_insurance: { label: 'Waiting on Insurance', color: 'bg-amber-500', icon: Clock },
  waiting_on_external: { label: 'Waiting on Outside Office', color: 'bg-orange-500', icon: Building },
  waiting_on_patient: { label: 'Waiting on Patient', color: 'bg-violet-500', icon: User },
  follow_up_due_today: { label: 'Follow-Up Due Today', color: 'bg-red-500', icon: AlertCircle },
  completed_recent: { label: 'Recently Completed', color: 'bg-emerald-500', icon: Check },
};

const STATUS_OPTIONS = ['new', 'in_progress', 'waiting_on_insurance', 'waiting_on_external', 'waiting_on_patient', 'follow_up', 'completed', 'canceled'];

function TaskCard({ task, onOpen }) {
  const payload = task.structured_payload || {};
  return (
    <div className="p-2.5 rounded-lg border bg-white hover:shadow-sm transition-shadow cursor-pointer"
      onClick={() => onOpen(task)} data-testid={`clerk-task-${task.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold">{task._patient_name || task.patient_id?.slice(0, 8)}</span>
            <Badge variant="outline" className={`text-[8px] h-4 ${task.priority === 'urgent' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-50 text-slate-500'}`}>
              {task.priority}
            </Badge>
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {payload.referral_type || task.task_type} {payload.destination ? `→ ${payload.destination}` : ''}
          </p>
          {payload.reason && <p className="text-[10px] text-slate-500 truncate">{payload.reason}</p>}
        </div>
        <div className="text-right shrink-0">
          {task.follow_up_date && <span className="text-[9px] text-amber-600 flex items-center gap-0.5"><CalendarIcon className="w-2.5 h-2.5" />{task.follow_up_date}</span>}
          <span className="text-[8px] text-muted-foreground block">{task.created_at?.slice(0, 10)}</span>
        </div>
      </div>
      {(task.communication_log || []).length > 0 && (
        <div className="mt-1 text-[9px] text-muted-foreground flex items-center gap-1">
          <MessageSquare className="w-2.5 h-2.5" /> {task.communication_log.length} log entries
        </div>
      )}
      {task.packet_status && task.packet_status !== 'not_started' && (
        <div className="mt-0.5">
          <Badge variant="outline" className={`text-[8px] ${
            task.packet_status === 'sent' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
            task.packet_status === 'ready' ? 'bg-blue-50 text-blue-600 border-blue-200' :
            'bg-amber-50 text-amber-600 border-amber-200'
          }`}>Packet: {task.packet_status.replace('_', ' ')}{task.packet_documents?.length > 0 ? ` (${task.packet_documents.length} docs)` : ''}</Badge>
        </div>
      )}
    </div>
  );
}

function TaskDetail({ task, onClose, onRefresh, specialties }) {
  const [status, setStatus] = useState(task.status);
  const [followUp, setFollowUp] = useState(task.follow_up_date || '');
  const [logNote, setLogNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [destFinalized, setDestFinalized] = useState(task.destination_finalized_by_clerk || '');
  const [apptStatus, setApptStatus] = useState(task.appointment_status || 'not_started');
  const [patientContact, setPatientContact] = useState(task.patient_contact_status || 'not_contacted');
  const [officeStatus, setOfficeStatus] = useState(task.outside_office_status || 'not_contacted');
  const [insuranceNote, setInsuranceNote] = useState(task.insurance_note || '');
  const [destSearch, setDestSearch] = useState('');
  const [destResults, setDestResults] = useState([]);
  const [packetDocs, setPacketDocs] = useState(new Set(task.packet_documents || []));
  const [suggestions, setSuggestions] = useState({ suggested_ids: [], available: {} });
  const [packetLoading, setPacketLoading] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);

  const searchDests = async (q) => {
    if (q.length < 2) { setDestResults([]); return; }
    try { const r = await api.get(`/work-clerk/destinations?query=${q}`); setDestResults(r.data.destinations || []); } catch {}
  };

  const loadPacketSuggestions = async () => {
    setPacketLoading(true);
    try {
      const r = await api.get(`/work-clerk/tasks/${task.id}/packet-suggestions`);
      setSuggestions(r.data);
    } catch {} finally { setPacketLoading(false); }
  };

  const togglePacketDoc = (docId) => {
    setPacketDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  };

  const savePacket = async () => {
    try {
      await api.put(`/work-clerk/tasks/${task.id}/packet`, { document_ids: [...packetDocs] });
      toast.success(`Packet updated (${packetDocs.size} documents)`);
      onRefresh();
    } catch { toast.error('Failed to save packet'); }
  };

  const setPacketSt = async (st, method) => {
    try {
      await api.post(`/work-clerk/tasks/${task.id}/packet-status`, { status: st, method });
      toast.success(`Packet: ${st}`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/work-clerk/tasks/${task.id}`, {
        status, follow_up_date: followUp || undefined,
        destination_finalized_by_clerk: destFinalized,
        appointment_status: apptStatus,
        patient_contact_status: patientContact,
        outside_office_status: officeStatus,
        insurance_note: insuranceNote,
      });
      toast.success('Task updated');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleAddLog = async () => {
    if (!logNote.trim()) return;
    try {
      await api.post(`/work-clerk/tasks/${task.id}/log`, { note: logNote });
      setLogNote('');
      toast.success('Log added');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const payload = task.structured_payload || {};
  const isProviderOriginated = task.source === 'provider';

  return (
    <Card className="border-2 border-primary/20" data-testid="clerk-task-detail">
      <CardHeader className="py-3 px-4 flex-row items-center justify-between border-b">
        <CardTitle className="text-sm flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" />
          {payload.referral_type || 'Referral'}: {task._patient_name || 'Patient'}
          {isProviderOriginated && <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200">Provider Originated</Badge>}
        </CardTitle>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}><X className="w-3.5 h-3.5" /></Button>
      </CardHeader>
      <CardContent className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {/* Section: Request Details */}
        <div>
          <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Request Details</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-muted-foreground">Patient:</span> <span className="font-medium">{task._patient_name || task.patient_id?.slice(0, 12)}</span></div>
            <div><span className="text-muted-foreground">Specialty:</span> <span className="font-medium">{payload.referral_type || task.task_type}</span></div>
            <div><span className="text-muted-foreground">Priority:</span> <Badge variant="outline" className={`text-[9px] ${task.priority === 'urgent' ? 'text-red-600' : ''}`}>{task.priority}</Badge></div>
            <div><span className="text-muted-foreground">Source:</span> <span>{task.source}{isProviderOriginated ? ' (provider)' : ''}</span></div>
            {task.any_acceptable && <div className="col-span-2"><Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600">Any acceptable provider</Badge></div>}
            {(task.referral_reason || payload.reason) && <div className="col-span-2"><span className="text-muted-foreground">Reason:</span> <span>{task.referral_reason || payload.reason}</span></div>}
            {task.diagnosis_context && <div className="col-span-2"><span className="text-muted-foreground">Dx Context:</span> <span>{task.diagnosis_context}</span></div>}
            {isProviderOriginated && task.encounter_id && <div className="col-span-2"><span className="text-muted-foreground">Encounter:</span> <span className="font-mono text-[10px]">{task.encounter_id.slice(0, 12)}...</span></div>}
            {task.source_context_text && <div className="col-span-2 p-1.5 bg-violet-50 rounded text-[10px] italic">"{task.source_context_text}"</div>}
          </div>
        </div>

        {/* Section: Destination */}
        <div className="pt-2 border-t">
          <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Destination</h4>
          {task.destination_requested_by_provider && (
            <div className="text-xs mb-1.5">
              <span className="text-muted-foreground">Provider requested:</span> <span className="font-medium">{task.destination_requested_by_provider}</span>
            </div>
          )}
          <div>
            <Label className="text-xs">Clerk Finalized Destination</Label>
            <Input value={destFinalized || destSearch} onChange={e => { setDestFinalized(e.target.value); setDestSearch(e.target.value); searchDests(e.target.value); }}
              placeholder="Search or type destination..." className="text-sm mt-1" data-testid="clerk-dest-finalized" />
            {destResults.length > 0 && (
              <div className="border rounded-md mt-1 max-h-28 overflow-y-auto">
                {destResults.map(d => (
                  <button key={d.id} className="w-full text-left px-2 py-1.5 text-xs hover:bg-accent border-b last:border-0"
                    onClick={() => { setDestFinalized(d.display_name); setDestResults([]); setDestSearch(''); }}>
                    <span className="font-medium">{d.display_name}</span>
                    <span className="text-muted-foreground ml-1">{d.city}{d.state ? `, ${d.state}` : ''}</span>
                    {d.phone && <span className="text-muted-foreground ml-1">({d.phone})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section: Communication / Status */}
        <div className="pt-2 border-t">
          <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Status Tracking</h4>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">Task Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="text-xs h-8 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Follow-Up Date</Label>
              <Input type="date" value={followUp} onChange={e => setFollowUp(e.target.value)} className="text-xs h-8 mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px]">Appointment</Label>
              <Select value={apptStatus} onValueChange={setApptStatus}>
                <SelectTrigger className="text-xs h-8 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['not_started', 'requested', 'scheduled', 'completed', 'declined'].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Patient Contact</Label>
              <Select value={patientContact} onValueChange={setPatientContact}>
                <SelectTrigger className="text-xs h-8 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['not_contacted', 'attempted', 'reached'].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Outside Office</Label>
              <Select value={officeStatus} onValueChange={setOfficeStatus}>
                <SelectTrigger className="text-xs h-8 mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['not_contacted', 'contacted', 'awaiting_response'].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Insurance Note */}
        <div className="pt-2 border-t">
          <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Insurance Notes</h4>
          <Input value={insuranceNote} onChange={e => setInsuranceNote(e.target.value)} placeholder="Insurance coordination notes..." className="text-xs h-8" />
        </div>

        {/* Packet Builder (Phase 3) */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              Referral Packet
              {task.packet_status && task.packet_status !== 'not_started' && (
                <Badge variant="outline" className={`text-[8px] ${
                  task.packet_status === 'sent' ? 'bg-emerald-50 text-emerald-600' :
                  task.packet_status === 'ready' ? 'bg-blue-50 text-blue-600' :
                  'bg-amber-50 text-amber-600'
                }`}>{task.packet_status.replace('_', ' ')}</Badge>
              )}
            </h4>
            <Button variant="outline" size="sm" className="h-6 text-[9px] px-2"
              onClick={() => { setPacketOpen(!packetOpen); if (!packetOpen) loadPacketSuggestions(); }}
              data-testid="packet-builder-toggle">
              {packetOpen ? 'Close' : `Build Packet (${packetDocs.size})`}
            </Button>
          </div>

          {packetOpen && (
            <div className="space-y-2 p-2 bg-slate-50 rounded-lg border" data-testid="packet-builder">
              {packetLoading ? (
                <p className="text-[10px] text-muted-foreground text-center py-2">Loading documents...</p>
              ) : (
                <>
                  {Object.entries(suggestions.available || {}).map(([cat, docs]) => {
                    if (!docs || docs.length === 0) return null;
                    const catLabels = { office_notes: 'Office Notes', labs: 'Labs', imaging: 'Imaging', medications: 'Medications', demographics: 'Demographics', other: 'Other Documents' };
                    return (
                      <div key={cat}>
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase mb-0.5">{catLabels[cat] || cat}</p>
                        {docs.map(doc => {
                          const selected = packetDocs.has(doc.id);
                          const isSuggested = (suggestions.suggested_ids || []).includes(doc.id);
                          return (
                            <button key={doc.id}
                              className={`w-full flex items-center gap-2 p-1.5 rounded text-left text-xs transition-colors mb-0.5 ${
                                selected ? 'bg-primary/10 border border-primary/30' : 'bg-white border border-transparent hover:border-slate-200'
                              }`}
                              onClick={() => togglePacketDoc(doc.id)}
                              data-testid={`packet-doc-${doc.id}`}
                            >
                              <span className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 ${selected ? 'border-primary bg-primary' : 'border-slate-300'}`}>
                                {selected && <Check className="w-2.5 h-2.5 text-white" />}
                              </span>
                              <span className="flex-1 truncate">{doc.title}</span>
                              {isSuggested && <Badge className="text-[7px] h-3.5 px-1 bg-amber-100 text-amber-700 border-0">Suggested</Badge>}
                              <span className="text-[9px] text-muted-foreground shrink-0">{doc.date}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                  {Object.values(suggestions.available || {}).every(a => !a || a.length === 0) && (
                    <p className="text-[10px] text-muted-foreground text-center py-2">No documents available for this patient</p>
                  )}
                  <div className="flex gap-1 pt-1 border-t">
                    <Button size="sm" className="flex-1 h-7 text-[10px]" onClick={savePacket}>
                      Save Packet ({packetDocs.size} docs)
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] text-blue-600" onClick={() => setPacketSt('ready')}>
                      Mark Ready
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] text-emerald-600" onClick={() => setPacketSt('sent', 'manual')}>
                      Mark Sent
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <Button size="sm" className="w-full" disabled={saving} onClick={handleSave}>
          <Check className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save All Changes'}
        </Button>

        {/* Communication Log */}
        <div className="pt-2 border-t">
          <h4 className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Communication Log</h4>
          <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
            {(task.communication_log || []).map(entry => (
              <div key={entry.id} className="text-[10px] p-1.5 bg-muted/30 rounded">
                <span className="text-muted-foreground">{entry.at?.slice(0, 16).replace('T', ' ')} —</span> {entry.note}
              </div>
            ))}
            {(task.communication_log || []).length === 0 && <p className="text-[10px] text-muted-foreground italic">No log entries yet</p>}
          </div>
          <div className="flex gap-1">
            <Input value={logNote} onChange={e => setLogNote(e.target.value)} placeholder="Add note..."
              className="text-xs h-7 flex-1" onKeyDown={e => e.key === 'Enter' && handleAddLog()} />
            <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={handleAddLog}>
              <MessageSquare className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function WorkClerkDashboard() {
  const [dashboard, setDashboard] = useState({ lanes: {}, counts: {} });
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [createForm, setCreateForm] = useState({
    patient_id: '', task_type: 'referral', priority: 'routine',
    referral_type: '', destination: '', location: '', reason: '', urgency: 'routine',
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, specRes] = await Promise.all([
        api.get('/work-clerk/dashboard'),
        api.get('/work-clerk/specialties'),
      ]);
      // Enrich tasks with patient names
      const lanes = dashRes.data.lanes || {};
      for (const key of Object.keys(lanes)) {
        for (const task of lanes[key]) {
          if (task.patient_id && !task._patient_name) {
            try {
              const pr = await api.get(`/patients/${task.patient_id}`);
              const pt = pr.data.patient;
              task._patient_name = pt?.name ? `${pt.name.first} ${pt.name.last}` : task.patient_id.slice(0, 8);
            } catch { task._patient_name = task.patient_id.slice(0, 8); }
          }
        }
      }
      setDashboard({ lanes, counts: dashRes.data.counts || {} });
      setSpecialties(specRes.data.specialties || []);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openTask = async (task) => {
    try {
      const res = await api.get(`/work-clerk/tasks/${task.id}`);
      setSelectedTask(res.data.task);
    } catch { setSelectedTask(task); }
  };

  const handleCreate = async () => {
    if (!createForm.patient_id) { toast.error('Select a patient'); return; }
    if (!createForm.referral_type) { toast.error('Select specialty'); return; }
    try {
      await api.post('/work-clerk/tasks', {
        patient_id: createForm.patient_id, task_type: createForm.task_type,
        priority: createForm.urgency,
        structured_payload: {
          referral_type: createForm.referral_type, destination: createForm.destination,
          location: createForm.location, reason: createForm.reason, urgency: createForm.urgency,
        },
      });
      toast.success('Task created');
      setShowCreate(false);
      setCreateForm({ patient_id: '', task_type: 'referral', priority: 'routine', referral_type: '', destination: '', location: '', reason: '', urgency: 'routine' });
      fetchDashboard();
    } catch { toast.error('Failed to create task'); }
  };

  const searchPatients = async (q) => {
    if (q.length < 2) { setPatients([]); return; }
    try { const r = await api.get(`/patients?search=${q}`); setPatients(r.data.patients || []); } catch {}
  };

  const totalActive = dashboard.counts.new + dashboard.counts.in_progress + dashboard.counts.waiting_on_external + dashboard.counts.waiting_on_patient + dashboard.counts.follow_up_due_today || 0;

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-4" data-testid="work-clerk-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" /> Work Clerk Dashboard
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">{totalActive} active tasks</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchDashboard}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="clerk-create-task"><Plus className="w-3.5 h-3.5 mr-1" /> New Task</Button>
        </div>
      </div>

      {/* Create Task Form */}
      {showCreate && (
        <Card className="border-2 border-primary/20" data-testid="clerk-create-form">
          <CardHeader className="py-2 px-4 flex-row items-center justify-between">
            <CardTitle className="text-sm">New Referral Task</CardTitle>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowCreate(false)}><X className="w-3.5 h-3.5" /></Button>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div>
              <Label className="text-xs">Patient</Label>
              <Input value={patientSearch} onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                placeholder="Search patient..." className="text-sm mt-1" data-testid="clerk-patient-search" />
              {patients.length > 0 && (
                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto">
                  {patients.map(p => (
                    <button key={p.id} className="w-full text-left px-2 py-1.5 text-xs hover:bg-accent"
                      onClick={() => { setCreateForm(f => ({...f, patient_id: p.id})); setPatientSearch(`${p.name.first} ${p.name.last}`); setPatients([]); }}>
                      {p.name.first} {p.name.last} <span className="text-muted-foreground">{p.mrn}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Specialty</Label>
                <Select value={createForm.referral_type} onValueChange={v => setCreateForm(f => ({...f, referral_type: v}))}>
                  <SelectTrigger className="text-sm mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{specialties.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Urgency</Label>
                <Select value={createForm.urgency} onValueChange={v => setCreateForm(f => ({...f, urgency: v}))}>
                  <SelectTrigger className="text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Destination (specific provider or "any acceptable")</Label>
              <Input value={createForm.destination} onChange={e => setCreateForm(f => ({...f, destination: e.target.value}))}
                placeholder="Dr. Cooper, Amarillo GI Associates" className="text-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea value={createForm.reason} onChange={e => setCreateForm(f => ({...f, reason: e.target.value}))}
                placeholder="Reason for referral..." className="text-sm mt-1 min-h-[50px]" />
            </div>
            <Button className="w-full" onClick={handleCreate} data-testid="clerk-create-submit">
              <Plus className="w-3.5 h-3.5 mr-1" /> Create Referral Task
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-4">
        {/* Left: Lane Board */}
        <div className={`${selectedTask ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {Object.entries(LANE_CONFIG).map(([key, cfg]) => {
            const tasks = dashboard.lanes[key] || [];
            const count = dashboard.counts[key] || 0;
            if (count === 0 && key === 'completed_recent') return null;
            const Icon = cfg.icon;
            return (
              <div key={key} data-testid={`clerk-lane-${key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${cfg.color}`} />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{cfg.label}</span>
                  <Badge variant="outline" className="text-[9px] h-4">{count}</Badge>
                </div>
                {tasks.length > 0 ? (
                  <div className="space-y-1 ml-4">
                    {tasks.map(t => <TaskCard key={t.id} task={t} onOpen={openTask} />)}
                  </div>
                ) : (
                  <p className="text-[10px] text-muted-foreground ml-4 italic">None</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Right: Task Detail */}
        {selectedTask && (
          <div className="w-1/2">
            <TaskDetail task={selectedTask} onClose={() => setSelectedTask(null)} onRefresh={() => { fetchDashboard(); openTask(selectedTask); }} specialties={specialties} />
          </div>
        )}
      </div>
    </div>
  );
}
